import { useEffect, useState, useCallback, useRef } from 'react';
import { getTaxonomies, deleteTaxonomy, cloneTaxonomy } from '../services/api';
import TaxonomyEditor from '../components/TaxonomyEditor';

const TaxonomieDashboard = () => {
  const [taxonomies, setTaxonomies] = useState([]);
  const [selectedTaxonomy, setSelectedTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTaxonomy, setEditingTaxonomy] = useState(null);
  const hasFetched = useRef(false);

  const refreshTaxonomies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching taxonomies...');
      const data = await getTaxonomies();
      console.log('Received taxonomies data:', data);
      
      if (Array.isArray(data)) {
        setTaxonomies(data);
        console.log('Set taxonomies state:', data);
      } else {
        console.error('Taxonomies data is not an array:', data);
        setTaxonomies([]);
      }
    } catch (error) {
      console.error('Error fetching taxonomies:', error);
      setError(error.message);
      setTaxonomies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      refreshTaxonomies();
    }
  }, [refreshTaxonomies]);

  const handleCreateNew = () => {
    setEditingTaxonomy(null);
    setShowEditor(true);
  };

  const handleEdit = (taxonomy) => {
    if (taxonomy.usecase_id === 'UC001') {
      // Allow viewing but not editing
      setEditingTaxonomy(taxonomy);
      setShowEditor(true);
    } else {
      setEditingTaxonomy(taxonomy);
      setShowEditor(true);
    }
  };

  const handleDelete = async (taxonomy) => {
    if (window.confirm(`Sind Sie sicher, dass Sie die Taxonomie für ${taxonomy.usecase_id} löschen möchten?`)) {
      try {
        await deleteTaxonomy(taxonomy._id || taxonomy.usecase_id);
        await refreshTaxonomies();
        if (selectedTaxonomy?.usecase_id === taxonomy.usecase_id) {
          setSelectedTaxonomy(null);
        }
      } catch (error) {
        alert('Fehler beim Löschen: ' + error.message);
      }
    }
  };

  const handleClone = async (taxonomy) => {
    const displayName = taxonomy.usecase_id || 'diese Taxonomie';
    if (window.confirm(`Möchten Sie eine Kopie von "${displayName}" erstellen? Die Kopie wird ohne UseCase-Zuordnung erstellt.`)) {
      try {
        const result = await cloneTaxonomy(taxonomy._id || taxonomy.usecase_id);
        await refreshTaxonomies();
        alert(`Taxonomie erfolgreich geklont! ${result.message}`);
      } catch (error) {
        alert('Fehler beim Klonen: ' + error.message);
      }
    }
  };

  const handleEditorSave = async () => {
    setShowEditor(false);
    setEditingTaxonomy(null);
    await refreshTaxonomies();
  };

  const handleEditorCancel = () => {
    setShowEditor(false);
    setEditingTaxonomy(null);
  };

  const renderCondition = (condition) => (
    <div key={`${condition.field}-${condition.type}`} style={{
      background: "var(--light-gray)",
      padding: "8px 12px",
      borderRadius: "6px",
      margin: "4px 0",
      fontSize: "13px"
    }}>
      <strong>{condition.field}</strong> {condition.type} {' '}
      {Array.isArray(condition.value) 
        ? `[${condition.value.join(', ')}]` 
        : JSON.stringify(condition.value)}
    </div>
  );

  const renderRule = (rule, ruleIndex) => (
    <div key={ruleIndex} style={{
      border: "1px solid var(--medium-gray)",
      borderRadius: "8px",
      padding: "16px",
      margin: "12px 0",
      background: "var(--white)"
    }}>
      <h5 style={{ 
        margin: "0 0 12px 0", 
        color: "var(--primary-coral)",
        fontSize: "14px" 
      }}>
        Regel {ruleIndex + 1}
      </h5>
      
      {/* Conditions */}
      <div style={{ marginBottom: "12px" }}>
        <strong style={{ fontSize: "13px", color: "var(--text-dark)" }}>Bedingungen:</strong>
        {rule.conditions && rule.conditions.map(renderCondition)}
      </div>
      
      {/* Content - Updated for new structure */}
      <div>
        <strong style={{ fontSize: "13px", color: "var(--text-dark)" }}>Inhalt:</strong>
        {rule.content && typeof rule.content === 'object' ? (
          <div style={{ 
            margin: "8px 0",
            padding: "12px",
            backgroundColor: "var(--secondary-pink-light)",
            borderRadius: "8px",
            border: "1px solid var(--secondary-pink)"
          }}>
            {/* Cooperation Partner */}
            {rule.content["cooperation-partner"] && (
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontWeight: "600", fontSize: "13px", color: "var(--primary-coral)" }}>
                  🤝 Kooperationspartner:
                </div>
                <div style={{ fontSize: "12px", marginLeft: "16px" }}>
                  {rule.content["cooperation-partner"]}
                </div>
              </div>
            )}

            {/* Link */}
            {rule.content.link && (
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontWeight: "600", fontSize: "13px", color: "var(--primary-coral)" }}>
                  🔗 Link:
                </div>
                <div style={{ marginLeft: "16px" }}>
                  <a href={rule.content.link} target="_blank" rel="noopener noreferrer" style={{
                    color: "var(--primary-coral)",
                    textDecoration: "none",
                    fontSize: "12px"
                  }}>
                    {rule.content.link}
                  </a>
                </div>
              </div>
            )}

            {/* Text */}
            {rule.content.text && (
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontWeight: "600", fontSize: "13px", color: "var(--primary-coral)" }}>
                  📝 Text:
                </div>
                <div style={{ 
                  fontSize: "12px", 
                  marginLeft: "16px",
                  lineHeight: "1.4",
                  color: "var(--text-dark)"
                }}>
                  {rule.content.text}
                </div>
              </div>
            )}

            {/* Bonus Tip */}
            {rule.content.bonustip && (
              <div>
                <div style={{ fontWeight: "600", fontSize: "13px", color: "var(--primary-coral)" }}>
                  💡 Bonus-Tipp:
                </div>
                <div style={{ marginLeft: "16px" }}>
                  <a href={rule.content.bonustip} target="_blank" rel="noopener noreferrer" style={{
                    color: "var(--primary-coral)",
                    textDecoration: "none",
                    fontSize: "12px"
                  }}>
                    {rule.content.bonustip}
                  </a>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ 
            fontSize: "12px", 
            color: "var(--text-light)", 
            fontStyle: "italic",
            margin: "8px 0"
          }}>
            Keine Inhalte definiert
          </div>
        )}
      </div>
    </div>
  );

  const renderCategory = (category, categoryIndex) => (
    <div key={categoryIndex} style={{
      border: "2px solid var(--secondary-pink)",
      borderRadius: "12px",
      padding: "20px",
      margin: "16px 0",
      background: "var(--secondary-pink-light)"
    }}>
      <h4 style={{ 
        margin: "0 0 16px 0", 
        color: "var(--primary-coral)",
        fontSize: "18px",
        borderBottom: "2px solid var(--primary-coral)",
        paddingBottom: "8px"
      }}>
        📋 {category.category}
      </h4>
      
      {category.rules && category.rules.map(renderRule)}
    </div>
  );

  // Add this function to render global settings
  const renderGlobalSettings = (taxonomy) => (
    <div style={{
      border: "2px solid var(--accent-coral)",
      borderRadius: "12px",
      padding: "16px",
      margin: "16px 0",
      background: "var(--secondary-pink-light)"
    }}>
      <h4 style={{ 
        margin: "0 0 12px 0", 
        color: "var(--primary-coral)",
        fontSize: "16px"
      }}>
        ⚙️ Globale Einstellungen
      </h4>
      
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "12px" }}>
        <div>
          <strong>Max. Angebote:</strong> {taxonomy.max_offers || 'Nicht definiert'}
        </div>
        <div>
          <strong>Max. Bonus-Tipps:</strong> {taxonomy.max_bonus_tips || 'Nicht definiert'}
        </div>
      </div>

      {taxonomy.bonus_tip_text && (
        <div style={{ marginBottom: "12px" }}>
          <strong>Bonus-Tipp Text:</strong>
          <div style={{ 
            fontSize: "13px", 
            fontStyle: "italic", 
            marginTop: "4px",
            padding: "8px",
            backgroundColor: "white",
            borderRadius: "4px"
          }}>
            "{taxonomy.bonus_tip_text}"
          </div>
        </div>
      )}

      {taxonomy.partner_priority && taxonomy.partner_priority.length > 0 && (
        <div>
          <strong>Partner-Prioritäten:</strong>
          <div style={{ marginTop: "8px" }}>
            {taxonomy.partner_priority
              .sort((a, b) => a.priority - b.priority)
              .map((partner, index) => (
                <div key={index} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "4px 8px",
                  margin: "2px 0",
                  backgroundColor: "white",
                  borderRadius: "4px",
                  fontSize: "12px"
                }}>
                  <span>{partner["cooperation-partner"]}</span>
                  <span style={{ fontWeight: "600", color: "var(--primary-coral)" }}>
                    Priorität {partner.priority}
                  </span>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );

  if (showEditor) {
    return (
      <TaxonomyEditor 
        taxonomyData={editingTaxonomy}
        onSave={handleEditorSave}
        onCancel={handleEditorCancel}
      />
    );
  }

  if (loading) {
    return (
      <div className="loading" style={{ 
        padding: "40px",
        textAlign: "center",
        fontSize: "18px",
        color: "var(--text-light)"
      }}>
        🔄 Lade Taxonomien...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: "40px",
        textAlign: "center",
        color: "var(--primary-coral)"
      }}>
        <h3>❌ Fehler beim Laden der Taxonomien</h3>
        <p>{error}</p>
        <button onClick={refreshTaxonomies}>
          🔄 Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex',
      minHeight: "600px"
    }}>
      {/* Left: Taxonomy List */}
      <div style={{ 
        width: "300px",
        borderRight: "1px solid var(--medium-gray)",
        padding: "24px"
      }}>        
        <div style={{ marginBottom: "20px" }}>
          <button
            onClick={handleCreateNew}
            style={{
              width: "100%",
              padding: "12px 16px",
              backgroundColor: "var(--secondary-pink)",
              color: "var(--text-dark)",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "600",
              marginBottom: "16px"
            }}
          >
            ➕ Neue Taxonomie erstellen
          </button>
        </div>

        <h3 style={{ 
          marginBottom: "20px",
          color: "var(--primary-coral)",
          fontSize: "18px"
        }}>
          🏷️ Taxonomien ({taxonomies.length})
        </h3>
        
        {taxonomies.length === 0 ? (
          <div style={{
            textAlign: "center",
            padding: "20px",
            color: "var(--text-light)",
            fontStyle: "italic"
          }}>
            Keine Taxonomien verfügbar
          </div>
        ) : (
          <div style={{ maxHeight: "500px", overflowY: "auto" }}>
            {taxonomies.map((taxonomy, index) => {
              const isProtected = taxonomy.usecase_id === 'UC001' || 
                                 (taxonomy.usecase_ids && taxonomy.usecase_ids.includes('UC001'));
              
              // Create a display name for the taxonomy
              let displayName = '';
              if (taxonomy.usecase_ids && taxonomy.usecase_ids.length > 0) {
                displayName = taxonomy.usecase_ids.filter(id => id).join(', ');
              } else if (taxonomy.usecase_id) {
                displayName = taxonomy.usecase_id;
              } else {
                displayName = '(Nicht zugeordnet)';
              }
              
              // Create a unique key for React
              const uniqueKey = taxonomy._id || 
                               (taxonomy.usecase_ids && taxonomy.usecase_ids.length > 0 ? taxonomy.usecase_ids.join('-') : '') ||
                               taxonomy.usecase_id || 
                               `taxonomy-${index}`;
              
              return (
                <div
                  key={uniqueKey}
                  style={{
                    padding: "12px",
                    margin: "4px 0",
                    borderRadius: "8px",
                    background: selectedTaxonomy?._id === taxonomy._id ? "var(--primary-coral)" : "var(--light-gray)",
                    color: selectedTaxonomy?._id === taxonomy._id ? "var(--white)" : "var(--text-dark)",
                    transition: "var(--transition)",
                    border: isProtected ? "2px solid #ffd700" : "1px solid transparent"
                  }}
                >
                  <div
                    onClick={() => {
                      console.log('Selected taxonomy:', taxonomy);
                      setSelectedTaxonomy(taxonomy);
                    }}
                    style={{ cursor: "pointer", marginBottom: "8px" }}
                    onMouseEnter={(e) => {
                      if (selectedTaxonomy?._id !== taxonomy._id) {
                        e.currentTarget.parentElement.style.background = "var(--secondary-pink-light)";
                        if (!isProtected) {
                          e.currentTarget.parentElement.style.borderColor = "var(--secondary-pink)";
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTaxonomy?._id !== taxonomy._id) {
                        e.currentTarget.parentElement.style.background = "var(--light-gray)";
                        if (!isProtected) {
                          e.currentTarget.parentElement.style.borderColor = "transparent";
                        }
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isProtected && <span style={{ fontSize: '16px' }}>🛡️</span>}
                      <strong>UseCase: {displayName}</strong>
                    </div>
                    <div style={{ fontSize: "12px", opacity: 0.8 }}>
                      {taxonomy.taxonomy ? `${taxonomy.taxonomy.length} Kategorien` : '0 Kategorien'}
                      {isProtected && <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>(Geschützt)</span>}
                      {!displayName || displayName === '(Nicht zugeordnet)' && 
                        <span style={{ marginLeft: '8px', fontStyle: 'italic', color: 'var(--primary-coral)' }}>(Kopie)</span>
                      }
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isProtected) {
                          alert('UC001 ist eine geschützte Taxonomie und kann nur angezeigt werden.');
                          return;
                        }
                        handleEdit(taxonomy);
                      }}
                      style={{
                        flex: 1,
                        padding: "4px 8px",
                        backgroundColor: isProtected ? "#ccc" : "var(--accent-coral)",
                        color: isProtected ? "#666" : "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: isProtected ? "not-allowed" : "pointer",
                        fontSize: "12px"
                      }}
                      disabled={isProtected}
                    >
                      {isProtected ? '👁️ Anzeigen' : '✏️ Bearbeiten'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isProtected) {
                          alert('UC001 ist eine geschützte Taxonomie und kann nicht gelöscht werden.');
                          return;
                        }
                        handleDelete(taxonomy);
                      }}
                      style={{
                        flex: 1,
                        padding: "4px 8px",
                        backgroundColor: isProtected ? "#ccc" : "#ff6b6b",
                        color: isProtected ? "#666" : "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: isProtected ? "not-allowed" : "pointer",
                        fontSize: "12px"
                      }}
                      disabled={isProtected}
                    >
                      {isProtected ? '🛡️ Geschützt' : '🗑️ Löschen'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClone(taxonomy);
                      }}
                      style={{
                        flex: 1,
                        padding: "4px 8px",
                        backgroundColor: "#4CAF50",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px"
                      }}
                    >
                      📋 Klonen
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Right: Taxonomy Details */}
      <div style={{ 
        flex: 1, 
        padding: "24px"
      }}>
        {selectedTaxonomy ? (
          <div>
            <div style={{ 
              marginBottom: "24px",
              padding: "20px",
              background: "var(--light-gray)",
              borderRadius: "12px"
            }}>
              <h2 style={{ 
                color: "var(--primary-coral)",
                margin: "0 0 8px 0"
              }}>
                📊 Taxonomie Details
              </h2>
              <p style={{ 
                margin: "0",
                color: "var(--text-dark)",
                fontSize: "16px"
              }}>
                <strong>UseCase ID(s):</strong> {
                  selectedTaxonomy.usecase_ids && selectedTaxonomy.usecase_ids.length > 0
                    ? selectedTaxonomy.usecase_ids.filter(id => id).join(', ')
                    : selectedTaxonomy.usecase_id || '(Nicht zugeordnet)'
                }
                {(!selectedTaxonomy.usecase_ids || selectedTaxonomy.usecase_ids.length === 0) && !selectedTaxonomy.usecase_id && 
                  <span style={{ marginLeft: '8px', fontStyle: 'italic', color: 'var(--primary-coral)' }}>- Geklonte Taxonomie</span>
                }
              </p>
            </div>

            {/* Global Settings */}
            {renderGlobalSettings(selectedTaxonomy)}

            <div style={{ maxHeight: "600px", overflowY: "auto" }}>
              {selectedTaxonomy.taxonomy && selectedTaxonomy.taxonomy.length > 0 ? (
                selectedTaxonomy.taxonomy.map(renderCategory)
              ) : (
                <div style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "var(--text-light)"
                }}>
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
                  <h4>Keine Taxonomie-Kategorien</h4>
                  <p>Diese Taxonomie enthält keine Kategorien.</p>
                </div>
              )}
            </div>

            {/* Debug info (development only) */}
            {process.env.NODE_ENV === 'development' && (
              <details style={{ 
                marginTop: "24px",
                background: "var(--light-gray)", 
                padding: "12px", 
                borderRadius: "8px",
                fontSize: "12px",
                color: "var(--text-light)"
              }}>
                <summary style={{ cursor: "pointer", fontWeight: "bold" }}>Debug Info</summary>
                <pre style={{ marginTop: "12px", overflow: "auto" }}>
                  {JSON.stringify(selectedTaxonomy, null, 2)}
                </pre>
              </details>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: "center",
            padding: "80px 40px",
            color: "var(--text-light)"
          }}>
            <div style={{ fontSize: "48px", marginBottom: "24px" }}>🏷️</div>
            <h3>Keine Taxonomie ausgewählt</h3>
            <p>Wählen Sie eine Taxonomie aus der Liste links aus oder erstellen Sie eine neue.</p>
            {taxonomies.length > 0 && (
              <p style={{ fontSize: "14px", marginTop: "16px" }}>
                {taxonomies.length} Taxonomie(n) verfügbar
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxonomieDashboard; 